import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { enrollmentKeys } from '@/hooks/use-enrollments';
import { useAuthHydration } from '@/hooks/use-auth-hydration';
import api from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import { ENROLLMENT_STATUS } from '@/types/enrollment-status';
import { QUOTA_PERIOD } from '@/types/quota-period';
import { SubscriptionStatus } from '@/types/subscription';
import { USER_ROLE } from '@/types/user-role';
import { recurrenceKeys } from './use-recurrences';

/**
 * Assinatura: o cliente contrata N sessões por período e marca cada
 * uma quando quiser. Aqui só a adesão e o saldo — a marcação em si continua
 * sendo o agendamento normal (`use-appointments`), que o backend liga ao
 * contrato sozinho.
 */

export const subscriptionKeys = {
  all: ['subscriptions'] as const,
  status: (serviceId: string | undefined) =>
    [...subscriptionKeys.all, 'status', serviceId ?? 'none'] as const,
};

/** Saldo da assinatura do cliente logado neste serviço. */
export function useSubscriptionStatus(serviceId: string | undefined) {
  const isHydrated = useAuthHydration();
  const { user } = useAuthStore();
  const isClient = user?.role === USER_ROLE.CLIENT;

  return useQuery({
    queryKey: subscriptionKeys.status(serviceId),
    queryFn: async () => {
      const res = await api.get<SubscriptionStatus>(
        `/client/schedule/services/${serviceId}/subscription`,
      );
      return res.data;
    },
    enabled: isHydrated && isClient && !!serviceId,
  });
}

/** Cliente assina o plano (o prestador ainda aprova a entrada). */
export function useCreateSubscription() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (serviceId: string) => {
      const res = await api.post<{ id: string; status: ENROLLMENT_STATUS }>(
        '/client/schedule/subscriptions',
        { serviceId },
      );
      return res.data;
    },
    onSuccess: (_data, serviceId) => {
      queryClient.invalidateQueries({
        queryKey: subscriptionKeys.status(serviceId),
      });
      queryClient.invalidateQueries({ queryKey: recurrenceKeys.mine() });
      // "Meus Contratos" lê enrollmentKeys, cache separado do de recorrência.
      queryClient.invalidateQueries({ queryKey: enrollmentKeys.all });
    },
  });
}

// Convidado (perfil público) -------------------------------------------------

export interface GuestSubscriptionInput {
  serviceId: string;
  guest: { name: string; phone: string };
  otpToken?: string;
}

export interface GuestSubscriptionResult {
  enrollmentId: string;
  status: ENROLLMENT_STATUS;
  quotaTerms: { sessions: number; period: QUOTA_PERIOD };
  /** false quando o telefone já pertence a uma conta completa (aí é login). */
  accountClaimable: boolean;
}

/** Convidado assina o plano pelo link público. */
export function useCreatePublicSubscription(slug: string | undefined) {
  return useMutation({
    mutationFn: async (input: GuestSubscriptionInput) => {
      const res = await api.post<GuestSubscriptionResult>(
        `/public/schedule/${slug}/subscriptions`,
        input,
      );
      return res.data;
    },
  });
}

/**
 * Saldo do convidado. POST porque o otpToken vai no corpo — credencial longa
 * não entra em URL.
 */
export function usePublicSubscriptionStatus(slug: string | undefined) {
  return useMutation({
    mutationFn: async (input: {
      serviceId: string;
      phone: string;
      otpToken?: string;
    }) => {
      const res = await api.post<SubscriptionStatus>(
        `/public/schedule/${slug}/subscriptions/status`,
        input,
      );
      return res.data;
    },
  });
}
